/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V94 - Disabled Link Style Fix) ---
 * - MISSION: To correct the styling of disabled "Coming Soon" footer links to ensure they appear visually consistent with other disabled elements..
 *
 * - THE FIX (CSS Class Correction): The `ComingSoonLink` component's `<span>` previously used `text-muted-foreground/50`, which applies alpha transparency only to the color. This has been corrected to `text-muted-foreground opacity-50`, which applies opacity to the entire element, resulting in a darker, more conventional disabled appearance that matches other disabled UI elements.
 *
 * - RESULT: The footer's disabled links are now visually consistent and accessible, improving the overall UI polish..
 */
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useModal } from '@/context/ModalContext';
import { useNavContext } from '@/context/NavigationProvider'; // --- NEW
import {
  FaTelegram, FaDiscord, FaYoutube, FaRedditAlien, FaLinkedin,
  FaFacebook, FaTiktok, FaInstagram, FaHandHoldingUsd
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SiClubhouse, SiBluesky, SiFarcaster } from 'react-icons/si';


// --- Data for Footer Elements ---
// socialIcons moved inside Footer component to allow for translation hooks

// --- MODIFIED: More descriptive link configuration ---
const footerLinksConfig = {
  ecosystem: [
    { key: 'filmmakers', type: 'scroll', targetId: 'founding-filmmakers' },
    { key: 'browseFilms', type: 'scroll', targetId: 'featured-films' },
    { key: 'howItWorks', type: 'modal', targetId: 'litepaper' },
    { key: 'token', type: 'modal', targetId: 'litepaper' },
  ],
  resources: [
    { key: 'litepaper', type: 'modal', targetId: 'litepaper' },
    { key: 'developerApi', type: 'soon' },
    { key: 'blog', type: 'external', to: 'https://bitjoinstudios.com/blog/' },
    { key: 'faq', type: 'page', to: '/faq' },
    { key: 'earn', type: 'page', to: '/earn' },
  ],
  legal: [
    { key: 'disclaimer', type: 'page', to: '/terms-of-service' },
    { key: 'privacyPolicy', type: 'page', to: '/privacy-policy' },
    { key: 'terms', type: 'page', to: '/terms-of-service' },
    { key: 'cookiePolicy', type: 'page', to: '/cookie-policy' },
    { key: 'ambassadorTerms', type: 'page', to: '/ambassador/terms' },
  ],
};

const ComingSoonLink = ({ children }) => {
  const { t } = useTranslation();
  return (
    <div className="coming-soon-wrapper inline-block">
      <span className="text-muted-foreground opacity-50 cursor-not-allowed">{children}</span>
      <span className="tooltip">{t('common.comingSoon')}</span>
    </div>
  );
};

// --- MODIFIED: Component now handles all link types ---
const FooterLinkColumn = ({ columnKey, links }) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { scrollToSection } = useNavContext();

  const handleLinkClick = (e, link) => {
    e.preventDefault();
    switch (link.type) {
      case 'modal':
        openModal(link.targetId);
        break;
      case 'scroll':
        scrollToSection(link.targetId);
        break;
      default:
        break;
    }
  };

  const renderLink = (link) => {
    const linkText = t(`footer.links.${link.key}`);
    switch (link.type) {
      case 'page':
        return <Link to={link.to}>{linkText}</Link>;
      case 'modal':
      case 'scroll':
        return <a href="#" onClick={(e) => handleLinkClick(e, link)}>{linkText}</a>;
      case 'external':
        return <a href={link.to} target="_blank" rel="noopener noreferrer">{linkText}</a>;
      case 'soon':
        return <ComingSoonLink>{linkText}</ComingSoonLink>;
      default:
        return <a href="#">{linkText}</a>;
    }
  };

  return (
    <div>
      <h4 className="text-lg font-semibold mb-4 text-white">{t(`footer.headings.${columnKey}`)}</h4>
      <ul className="footer-links">
        {links.map(link => (
          <li key={link.key}>
            {renderLink(link)}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { cookieBannerHeight } = useNavContext();

  const socialIcons = [
    { name: 'Telegram', icon: FaTelegram, url: t('common.social.telegramUrl') },
    { name: 'Discord', icon: FaDiscord, url: 'https://discord.gg/3CnX4F3QkY' },
    { name: 'X (Twitter)', icon: FaXTwitter, url: 'https://x.com/IndiKinofficial' },
    { name: 'YouTube', icon: FaYoutube, url: 'https://www.youtube.com/@IndiKin' },
    { name: 'Reddit', icon: FaRedditAlien, url: 'https://www.reddit.com/r/indikin' },
    { name: 'LinkedIn', icon: FaLinkedin, url: 'https://www.linkedin.com/showcase/indikin' },
    { name: 'Facebook', icon: FaFacebook, url: 'https://www.facebook.com/indikinofficial' },
    { name: 'TikTok', icon: FaTiktok, url: 'https://www.tiktok.com/@indikinoffical' },
    { name: 'Instagram', icon: FaInstagram, url: 'https://www.instagram.com/indikinoffical' },
    { name: 'Clubhouse', icon: SiClubhouse, url: 'https://www.clubhouse.com/house/indikinonline' },
    { name: 'Bluesky', icon: SiBluesky, url: 'https://bsky.app/profile/indikin.bsky.social' },
    { name: 'Farcaster', icon: SiFarcaster, url: t('common.social.farcasterUrl') },
  ];


  return (
    <footer
      id="footer"
      className="footer"
      style={{
        marginBottom: `${cookieBannerHeight}px`,
        transition: 'margin-bottom 0.5s ease-in-out'
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className="footer-grid">

          <div className="lg:col-span-1">
            <a href="#" className="logo mb-4 flex items-center">
              <picture>
                <source srcSet="/assets/img/logo.webp" type="image/webp" />
                <source srcSet="/assets/img/logo.png" type="image/png" />
                <img
                  src="/assets/img/logo.png"
                  alt="Indikin Logo"
                  className="w-10 h-10 rounded-full mr-2"
                  width="40"
                  height="40"
                />
              </picture>
              <span className="text-white">INDIKIN</span><span className="text-primary">.online</span>
            </a>
            <p className="text-gray-400 text-base mb-4">
              {t('footer.description')}
            </p>
            <div className="social-icons">
              {socialIcons.map(social => {
                const Icon = social.icon;
                return (
                  <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label={social.name}>
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {Object.entries(footerLinksConfig).map(([columnKey, links]) => (
            <FooterLinkColumn key={columnKey} columnKey={columnKey} links={links} />
          ))}

        </div>

        <div className="mt-12 pt-8 border-t border-[#2a2a2a] text-center">
          <h4 className="text-lg font-semibold mb-3 text-white">{t('footer.donate.title')}</h4>
          <p className="text-gray-400 mb-4">{t('footer.donate.subtitle')}</p>
          <button onClick={() => openModal('donate')} className="btn btn-primary rounded-full">
            <FaHandHoldingUsd className="mr-2" /> {t('footer.donate.button')}
          </button>
        </div>

        <div className="mt-12 pt-8 border-t border-[#2a2a2a] text-center text-gray-400 text-sm">
          <p>{t('footer.copyright')}</p>
          <p className="mt-2">
            <Trans
              i18nKey="footer.credit"
              components={[
                <a key="indikin-link" href="https://indikin.online/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors pink" />,
                <a key="bitjoin-link" href="https://bitjoinstudios.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors pink" />,
              ]}
            />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
